import { useEffect, useMemo } from 'react';
import cm from 'classnames';

import { FinalCut, RenderStatus } from 'services/finalcut.service';
import { ObjectType } from 'services/access.service';
import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { prettyBytes, prettyDate, prettyDuration, prettyResolution } from 'components/app/_helpers/_app.helper';
import { updateEntity, setRemovingInstance, selectFinalCut } from 'components/app/_mediator/app-data.slice';
import { EditCell } from 'components/app/app-components/edit-cell/edit-cell';

import { setTableState, setSelectedFinalcut, setAccessTypeModal } from '../_mediator/projects.slice';
import { getFinalCutsSortCb, PROJECTS_FINALCUTS_HEADERS } from '../_mediator/projects.params';
import { useProjectsSearch } from '../use-projects-search';
import { sortProjectEntities } from '../_mediator/projects.helper';

import { ReactComponent as MoveToIcon } from 'assets/ui/move-to.svg';
import { ReactComponent as TrashIcon } from 'assets/ui/trash.svg';

import { FcStatus } from 'components/app/app-components/fc-status/fc-status';

import { HeaderAccessIco } from '../elements/header-access-ico';
import { HeaderAction } from '../elements/header-action';
import { HeaderSearch } from '../elements/header-search';
import { HeaderActionFolder } from '../elements/header-action-folders';

import { ProjectsTable } from './elements/projects-table';
import { SectionHeader } from './elements/section-header';
import { ThumbFc } from './elements/thumb-table';
import { Section } from './elements/section';
import styles from './_sections.module.scss';

export const SectionFinalCuts = () => {
  const fcData = useAppSelector(selectFinalCut);
  const { finalcuts, stories, projects, removingInstance } = useAppSelector((state) => state.app__data);
  const { selectedStory, selectedFinalcut, selectedProject, finalCutsTableState, finalCutsTableSort } = useAppSelector(
    (state) => state.projects
  );
  const dispatch = useAppDispatch();

  const { searchValue, updateSearchValue, getFilteredList, clearSearchValue } =
    useProjectsSearch('finalCutsSearchFilter');

  const isDisabled = useMemo(() => {
    return !selectedFinalcut || removingInstance?.instance !== 'finalcut';
  }, [selectedFinalcut, removingInstance?.instance]);

  const filteredFinalCuts = useMemo(
    () => (finalcuts ? getFilteredList<FinalCut>(finalcuts) : []),
    [finalcuts, searchValue]
  );

  const sortedFinalcuts = useMemo(() => {
    return sortProjectEntities(
      filteredFinalCuts,
      finalCutsTableSort.order,
      getFinalCutsSortCb(PROJECTS_FINALCUTS_HEADERS[finalCutsTableSort.column])
    );
  }, [filteredFinalCuts, finalCutsTableSort]);

  const selectedStoryName = useMemo(
    () => stories?.find((story) => story.storyId === selectedStory)?.name,
    [projects, selectedStory]
  );

  const handleSelect = (id: number) => {
    dispatch(setRemovingInstance({ id: id, instance: 'finalcut', modalOpen: false }));
    dispatch(setSelectedFinalcut(id));
  };

  useEffect(() => {
    dispatch(setTableState({ key: 'finalCutsTableState', value: !finalcuts.length ? false : true }));
  }, [finalcuts.length]);

  useEffect(() => {
    return () => clearSearchValue();
  }, [selectedProject, selectedStory]);

  return (
    <Section expanded={finalCutsTableState}>
      <SectionHeader
        expanded={finalCutsTableState}
        keyTableState="finalCutsTableState"
        title="Final Cuts"
        projectName={selectedStoryName}
        len={sortedFinalcuts.length}
        empty={!finalcuts?.length}
      >
        <HeaderSearch
          searchValue={searchValue}
          updateSearchValue={updateSearchValue}
          clearSearchValue={clearSearchValue}
        />
        <HeaderAction
          type="access"
          icon={<HeaderAccessIco accessType={fcData?.accessType} />}
          onSubmit={() => dispatch(setAccessTypeModal(ObjectType.FinalCut))}
          disabled={!selectedFinalcut}
          tip="Manage final cut access"
        />
        <HeaderAction
          type="delete"
          icon={<TrashIcon />}
          onSubmit={() => {
            removingInstance?.instance === 'finalcut' &&
              dispatch(setRemovingInstance({ ...removingInstance, modalOpen: true }));
          }}
          disabled={isDisabled}
          tip={isDisabled ? 'Select a final cut for deletion' : 'Delete final cut'}
        />
        <HeaderActionFolder type="finalcut" disabled={isDisabled}>
          <HeaderAction type="moveTo" icon={<MoveToIcon />} disabled={isDisabled} />
        </HeaderActionFolder>
      </SectionHeader>
      <div className={styles.SectionBody}>
        {finalcuts && finalcuts.length !== 0 && (
          <ProjectsTable
            sortKey="finalCutsTableSort"
            headers={PROJECTS_FINALCUTS_HEADERS}
            selectedRow={selectedFinalcut ?? undefined}
          >
            {sortedFinalcuts?.map((row) => (
              <tr
                key={row.id}
                onClick={() => handleSelect(row.id)}
                data-testid="project-finalcut"
                className={cm({
                  [styles.selected]: row.id === selectedFinalcut,
                  [styles.removing]:
                    removingInstance?.instance === 'finalcut' &&
                    removingInstance?.modalOpen &&
                    removingInstance?.id === row.id,
                  [styles.error]: row.status === RenderStatus.error,
                })}
              >
                <ThumbFc el={row} />
                <EditCell
                  element={{ name: row.name, id: row.id }}
                  onSubmit={(element) =>
                    dispatch(updateEntity({ id: element.id, name: element.name, entity: 'finalcuts' }))
                  }
                  selectedID={selectedFinalcut}
                  selectedName={finalcuts?.find((finalcut) => finalcut.id === selectedFinalcut)?.name ?? ''}
                />
                <td style={{ textTransform: 'capitalize' }}>
                  <FcStatus fc={row} />
                </td>
                <td>{row.payment?.price?.name ?? row.subscription?.price?.name}</td>
                <td>{prettyDuration(row.duration)}</td>
                <td>{prettyBytes(row.size)}</td>
                <td>{prettyResolution(row.width, row.height)}</td>
                <td>{prettyDate(row.mediaOriginDate)}</td>
                <td>{prettyDate(row.createdAt)}</td>
                <td>{prettyDate(row.accessedAt)}</td>
                <td>{prettyDate(row.updatedAt)}</td>
              </tr>
            ))}
          </ProjectsTable>
        )}
      </div>
    </Section>
  );
};
